import { useState } from 'react';
import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { AppLayout } from '@/components/AppLayout';
import { DesktopPage } from '@/components/desktop/DesktopPage';
import { DesktopPageHeader } from '@/components/desktop/DesktopPageHeader';
import { useIsDesktop } from '@/components/ui/use-mobile';
import { Card } from '@/components/ui/card';
import { DetailSkeleton } from '@/components/ui/skeleton-presets';
import { CompanyInfoDrawer } from '@/components/CompanyInfoDrawer';
import { useCompanyGet } from '@/lib/hooks/company';
import { useCrewGetMe } from '@/lib/hooks';
import { TopBarBackButton } from '@/components/topbar';
import { MoreVertical, ArrowLeft } from '@/components/icons';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';

export const Route = createFileRoute('/_auth/operations/settings/companyinfo')({
  component: CompanyInfoRoute,
});

function CompanyInfoRoute() {
  const { data: company, isLoading } = useCompanyGet();
  const { data: me } = useCrewGetMe();
  const [showEditDrawer, setShowEditDrawer] = useState(false);
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();

  const topBarRightIcon = me?.isOwner ? (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="secondary" size="sm" roundness="round" className="size-8 p-0">
          <MoreVertical className="size-5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={() => setShowEditDrawer(true)}>
          Edit Company Info
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  ) : undefined;

  const body = (
    <>
      {isLoading ? (
        <DetailSkeleton rows={6} />
      ) : (
        <Card className="flex flex-col gap-2 mb-6">
          <InfoRow label="Company Name" value={company?.name} />
          <InfoRow label="Timezone" value={company?.timezone} />
          {company?.email && <InfoRow label="Email" value={company.email} />}
          {company?.phone && <InfoRow label="Phone" value={company.phone} />}
          {company?.address && <InfoRow label="Address" value={company.address} />}
          {(company?.city || company?.state) && (
            <InfoRow
              label="City / State"
              value={[company?.city, company?.state].filter(Boolean).join(', ')}
            />
          )}
          {company?.country && <InfoRow label="Country" value={company.country} />}
          {company?.postalCode && <InfoRow label="Zip" value={company.postalCode} />}
          {company?.legalName && <InfoRow label="Legal Name" value={company.legalName} />}
          {company?.registrationNumber && (
            <InfoRow label="Reg. Number" value={company.registrationNumber} />
          )}
          {company?.taxId && <InfoRow label="Tax ID" value={company.taxId} />}
        </Card>
      )}

      <CompanyInfoDrawer
        isOpen={showEditDrawer}
        onClose={() => setShowEditDrawer(false)}
      />
    </>
  );

  const breadcrumb = (
    <span className="flex items-center gap-2">
      <button
        type="button"
        onClick={() => navigate({ to: '/operations/settings' })}
        className="flex size-7 items-center justify-center rounded-full hover:bg-surface-L1"
        aria-label="Back to settings"
      >
        <ArrowLeft className="size-4" />
      </button>
      <span>
        <Link to="/operations" className="hover:underline">
          Operations
        </Link>
        <span className="text-content-primary-subtle"> / </span>
        <Link to="/operations/settings" className="hover:underline">
          Settings
        </Link>
        <span className="text-content-primary-subtle"> / Company Info</span>
      </span>
    </span>
  );

  if (isDesktop) {
    return (
      <DesktopPage>
        <DesktopPageHeader
          title="Company Info"
          breadcrumb={breadcrumb}
          action={topBarRightIcon}
        />
        {body}
      </DesktopPage>
    );
  }

  return (
    <AppLayout
      topBar={
        <TopBarBackButton
          title="Company Info"
          rightElement={topBarRightIcon}
        />
      }
      className="pt-6"
    >
      {body}
    </AppLayout>
  );
}

function InfoRow({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="flex items-start justify-between gap-4 w-full">
      <p className="text-sm text-content-primary-bold tracking-[0.07px] leading-6 shrink-0">{label}</p>
      <p className="text-sm font-medium text-content-primary-bold tracking-[0.07px] leading-6 text-right">
        {value || '—'}
      </p>
    </div>
  );
}
